import { useEffect } from "react";
import { cloneDeep, isEmpty } from "lodash";
import {
  useAppDispatch,
  useAppSelector,
} from "../redux-store/CustomReduxHooks";
import {
  useLazyFetchMasterDataQuery,
  useLazyUnifiedOfferDetailQuery,
} from "../features/Feature_GeneralAppElements/GeneralAppElements_Api";
import {
  useLazyFetchCenterBannerQuery,
  useLazyFetchErrorVariableQuery,
  useLazyFetchFooterBannerQuery,
  useLazyFetchIntegrationVariableQuery,
  useLazyFetchL2BannerQuery,
  useLazyFetchMyTrackQuery,
  useLazyFetchNewOfferingQuery,
  useLazyFetchNudgeBannerQuery,
  useLazyFetchPageMessageQuery,
  useLazyFetchPageVariableQuery,
  useLazyFetchTopBannerQuery,
} from "../features/Features_Cms/Cms_Api";
import {
  setIsOfferMartApiRunning,
  setMasterData,
  setUnifiedOfferDetails,
} from "../features/Feature_GeneralAppElements/GeneralAppElements_Slice";
import {
  setCenterBannerData,
  setCmsOfferMartTopBannerData,
  setErrorVariables,
  setFooterBannerData,
  setIntegrationVariables,
  setL2BannerData,
  setMyTrackData,
  setNewOfferingData,
  setNudgeBannerData,
  setPageMessage,
  setPageVariables,
  setTopBannerData,
} from "../features/Features_Cms/Feature_Cms_Slice";
import { filterValidOfferMartData } from "../utils/conversionUtils";
import { getCMSLink, getMobileOperatingSystem } from "../utils/helper";
import { iOSType, platformWeb } from "../utils/constants";
import pageVariablesDefault from "../assets/testData/CMS_Defaults/pageVariables.json";

const pageVariablesDefaultMap = pageVariablesDefault.map((r: any) => {
  return {
    ...r,
    attributes: { ...r.attributes, slNo: Number(r.attributes.slNo) },
  };
});

const useCsAppCmsBootstrapData = () => {
  const appDispatch = useAppDispatch();
  const userDetails = useAppSelector((state) => state.CsAppSlice.userDetails);
  const csAppSlice = useAppSelector((state) => state.CsAppSlice);
  const cmsSlice = useAppSelector((state) => state.CmsSlice);
  const masterData = useAppSelector(
    (state) => state.GeneralAppElementsSlice.masterData,
  );
  const isProfileApiCalled = useAppSelector(
    (state) => state.LanguageSlice.isProfileApiCalled,
  );
  const os = csAppSlice.NativeDeviceInfo?.deviceName
    ? getMobileOperatingSystem() === iOSType
      ? "ios"
      : "android"
    : platformWeb;

  const [fetchMasterDataApi] = useLazyFetchMasterDataQuery();
  const [fetchUnifiedOfferDetails] = useLazyUnifiedOfferDetailQuery();
  const [fetchPageVariable] = useLazyFetchPageVariableQuery();
  const [fetchCmsIntegration] = useLazyFetchIntegrationVariableQuery();
  const [fetchErrorVariable] = useLazyFetchErrorVariableQuery();
  const [fetchPageMessage] = useLazyFetchPageMessageQuery();
  const [fetchMyTrack] = useLazyFetchMyTrackQuery();
  const [fetchTopBanner] = useLazyFetchTopBannerQuery();
  const [fetchCenterBanner] = useLazyFetchCenterBannerQuery();
  const [fetchNewOffering] = useLazyFetchNewOfferingQuery();
  const [fetchFooterBanner] = useLazyFetchFooterBannerQuery();
  const [fetchL2Banner] = useLazyFetchL2BannerQuery();
  const [fetchNudgeBanner] = useLazyFetchNudgeBannerQuery();

  const fetchOfferDetailsWithTopBanner = async () => {
    if (userDetails?.mobileNumber) {
      try {
        appDispatch(setIsOfferMartApiRunning(true));
        const unifiedOfferDetailsResponse = await fetchUnifiedOfferDetails({
          mobile_number: userDetails.mobileNumber,
        }).unwrap();
        const topBannerResponse = await fetchTopBanner().unwrap();

        appDispatch(setIsOfferMartApiRunning(false));
        const unifiedOfferDetails = cloneDeep(
          unifiedOfferDetailsResponse?.payload?.unifiedOfferDetialsList,
        );
        appDispatch(setUnifiedOfferDetails(unifiedOfferDetails));
        const topBannersData = topBannerResponse?.payload.data;
        appDispatch(setTopBannerData(topBannersData));
        const finalOfferMartCard = filterValidOfferMartData(
          [...unifiedOfferDetails],
          topBannersData,
          csAppSlice,
          unifiedOfferDetailsResponse?.payload?.type || "",
        );
        appDispatch(setCmsOfferMartTopBannerData(finalOfferMartCard));
      } catch (error) {
        appDispatch(setIsOfferMartApiRunning(false));
        console.error("Error fetching offer details:", error);
      }
    }
  };

  useEffect(() => {
    const timer = setTimeout(() => {
      if (isEmpty(cmsSlice.CmsPageVariableData)) {
        fetchPageVariable().then((res: any) => {
          let updatedData: any[] = [];
          if (res.isSuccess && res.data?.payload?.data) {
            updatedData = res.data.payload.data.map((item: any) => ({
              ...item,
              attributes: {
                ...item.attributes,
                originalUrl: item.attributes.url || "",
                url: getCMSLink(item.attributes.url || "", os),
              },
            }));
          } else {
            updatedData = pageVariablesDefaultMap;
          }
          appDispatch(setPageVariables(updatedData));
        });
      }
    }, 100);

    return () => clearTimeout(timer);
  }, [appDispatch, cmsSlice.CmsPageVariableData, os, pageVariablesDefaultMap]);

  useEffect(() => {
    const timer = setTimeout(() => {
      fetchOfferDetailsWithTopBanner();
    }, 500);

    return () => clearTimeout(timer);
  }, [userDetails?.mobileNumber]);

  useEffect(() => {
    let timer: NodeJS.Timeout;
    if (!isEmpty(userDetails?.mobileNumber) && isProfileApiCalled) {
      timer = setTimeout(() => {
        Promise.allSettled([
          isEmpty(cmsSlice.cmsL2BannerData)
            ? fetchL2Banner({ mobile: userDetails?.mobileNumber || "" })
            : Promise.resolve(null),
          isEmpty(cmsSlice.cmsNudgeBannerData)
            ? fetchNudgeBanner()
            : Promise.resolve(null),
        ]).then(([l2BannerRes, nudgeBannerRes]: any) => {
          if (l2BannerRes?.status === "rejected") {
            console.error("CMS L2 banner fetch failed", l2BannerRes.reason);
          }
          if (nudgeBannerRes?.status === "rejected") {
            console.error(
              "CMS nudge banner fetch failed",
              nudgeBannerRes.reason,
            );
          }

          const l2BannerData =
            l2BannerRes?.status === "fulfilled" ? l2BannerRes.value : null;
          const nudgeBannerData =
            nudgeBannerRes?.status === "fulfilled"
              ? nudgeBannerRes.value
              : null;

          if (l2BannerData?.isSuccess && l2BannerData?.data?.payload?.data) {
            appDispatch(setL2BannerData(l2BannerData.data.payload.data));
          }
          if (
            nudgeBannerData?.isSuccess &&
            nudgeBannerData?.data?.payload?.data
          ) {
            appDispatch(setNudgeBannerData(nudgeBannerData.data.payload.data));
          }
        });
      }, 2000);
    }
    return () => clearTimeout(timer);
  }, [
    appDispatch,
    cmsSlice.cmsL2BannerData,
    cmsSlice.cmsNudgeBannerData,
    userDetails?.mobileNumber,
    isProfileApiCalled,
  ]);

  useEffect(() => {
    if (userDetails?.customerAndLobDetails && isProfileApiCalled) {
      if (!masterData?.masterDetail) {
        fetchMasterDataApi("all").then((res: any) => {
          appDispatch(setMasterData(res.data?.payload));
        });
      }

      Promise.allSettled([
        isEmpty(cmsSlice.CmsIntegrationVariableData)
          ? fetchCmsIntegration()
          : Promise.resolve(null),
        isEmpty(cmsSlice.CmsErrorVariablesData)
          ? fetchErrorVariable()
          : Promise.resolve(null),
        isEmpty(cmsSlice.CmsPageMessagesData)
          ? fetchPageMessage()
          : Promise.resolve(null),
        isEmpty(cmsSlice.cmsMyTrackData)
          ? fetchMyTrack()
          : Promise.resolve(null),
        isEmpty(cmsSlice.cmsCenterBannerData)
          ? isEmpty(userDetails?.mobileNumber)
            ? Promise.resolve(null)
            : fetchCenterBanner({ mobile: userDetails?.mobileNumber || "" })
          : Promise.resolve(null),
        isEmpty(cmsSlice.cmsNewOfferingData)
          ? fetchNewOffering()
          : Promise.resolve(null),
        isEmpty(cmsSlice.cmsFooterBannerData)
          ? fetchFooterBanner()
          : Promise.resolve(null),
      ]).then(
        ([
          cmsIntRes,
          errorVarRes,
          pageMessageRes,
          myTrackRes,
          centerBannerRes,
          newOfferingRes,
          footerBannerRes,
        ]: any) => {
          const cmsIntegrationData =
            cmsIntRes?.status === "fulfilled" ? cmsIntRes.value : null;
          const errorVariablesData =
            errorVarRes?.status === "fulfilled" ? errorVarRes.value : null;
          const pageMessageData =
            pageMessageRes?.status === "fulfilled"
              ? pageMessageRes.value
              : null;
          const myTrackData =
            myTrackRes?.status === "fulfilled" ? myTrackRes.value : null;
          const centerBannerData =
            centerBannerRes?.status === "fulfilled"
              ? centerBannerRes.value
              : null;
          const newOfferingData =
            newOfferingRes?.status === "fulfilled"
              ? newOfferingRes.value
              : null;
          const footerBannerResultData =
            footerBannerRes?.status === "fulfilled"
              ? footerBannerRes.value
              : null;

          if (cmsIntRes?.status === "rejected") {
            console.error(
              "CMS integration variable fetch failed",
              cmsIntRes.reason,
            );
          }
          if (errorVarRes?.status === "rejected") {
            console.error(
              "CMS error variable fetch failed",
              errorVarRes.reason,
            );
          }
          if (pageMessageRes?.status === "rejected") {
            console.error(
              "CMS page message fetch failed",
              pageMessageRes.reason,
            );
          }
          if (myTrackRes?.status === "rejected") {
            console.error("CMS my-track fetch failed", myTrackRes.reason);
          }
          if (centerBannerRes?.status === "rejected") {
            console.error(
              "CMS center banner fetch failed",
              centerBannerRes.reason,
            );
          }
          if (newOfferingRes?.status === "rejected") {
            console.error(
              "CMS new offering fetch failed",
              newOfferingRes.reason,
            );
          }
          if (footerBannerRes?.status === "rejected") {
            console.error(
              "CMS footer banner fetch failed",
              footerBannerRes.reason,
            );
          }

          if (
            cmsIntegrationData?.isSuccess &&
            cmsIntegrationData?.data?.payload?.data
          ) {
            const updatedData = cmsIntegrationData.data.payload.data.map(
              (item: any) => ({
                ...item,
                attributes: {
                  ...item?.attributes,
                  originalUrl: item?.attributes?.url || "",
                  url: getCMSLink(item?.attributes?.url || "", os),
                },
              }),
            );
            appDispatch(setIntegrationVariables(updatedData));
          }

          if (
            errorVariablesData?.isSuccess &&
            errorVariablesData?.data?.payload?.data
          ) {
            appDispatch(
              setErrorVariables(errorVariablesData.data.payload.data),
            );
          }

          if (
            pageMessageData?.isSuccess &&
            pageMessageData?.data?.payload?.data
          ) {
            const updatedData = pageMessageData.data.payload.data.map(
              (item: any) => ({
                ...item,
                attributes: {
                  ...item?.attributes,
                  originalUrl: item?.attributes?.nudgeLink,
                  nudgeLink: getCMSLink(item?.attributes?.nudgeLink || "", os),
                },
              }),
            );
            appDispatch(setPageMessage(updatedData));
          }

          if (myTrackData?.isSuccess && myTrackData?.data?.payload?.data) {
            appDispatch(setMyTrackData(myTrackData.data.payload.data));
          }

          if (
            centerBannerData?.isSuccess &&
            centerBannerData?.data?.payload?.data
          ) {
            appDispatch(
              setCenterBannerData(centerBannerData.data.payload.data),
            );
          }

          if (
            newOfferingData?.isSuccess &&
            newOfferingData?.data?.payload?.data
          ) {
            appDispatch(setNewOfferingData(newOfferingData.data.payload.data));
          }

          if (
            footerBannerResultData?.isSuccess &&
            footerBannerResultData?.data?.payload?.data
          ) {
            appDispatch(
              setFooterBannerData(footerBannerResultData.data.payload.data),
            );
          }
        },
      );
    }
  }, [
    appDispatch,
    cmsSlice.CmsErrorVariablesData,
    cmsSlice.CmsIntegrationVariableData,
    cmsSlice.CmsPageMessagesData,
    cmsSlice.cmsCenterBannerData,
    cmsSlice.cmsFooterBannerData,
    cmsSlice.cmsMyTrackData,
    cmsSlice.cmsNewOfferingData,
    masterData?.masterDetail,
    os,
    userDetails?.customerAndLobDetails,
    userDetails?.mobileNumber,
    isProfileApiCalled,
  ]);
};

export default useCsAppCmsBootstrapData;
